/**
 * DeploymentGuidePage Component
 * AIDLC Administrative Backend
 *
 * Interactive deployment guide for the AIDLC Admin Portal.
 * Converts the DEPLOYMENT.md into a scannable, interactive page with:
 * - Quick Reference card with copy-to-clipboard commands
 * - Interactive pre-deployment checklist (persisted in localStorage)
 * - Milestone checkpoint visualization with pass/fail indicators
 * - Collapsible sections for detailed content
 * - Code blocks with copy-to-clipboard
 *
 * UX rationale: Deployment guides are typically long markdown files that
 * engineers skim under time pressure. This page front-loads the most
 * critical information (quick ref, checklist) and uses progressive
 * disclosure (collapsible sections) so operators can drill into details
 * only when needed. The interactive checklist provides a sense of
 * progress and prevents skipped steps.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './Card';
import { Badge } from './Badge';
import { Button } from './Button';

// ─── Constants ────────────────────────────────────────────────────

const STORAGE_KEY = 'aidlc-deploy-checklist';

// ─── Types ────────────────────────────────────────────────────────

interface ChecklistItem {
  id: string;
  label: string;
  detail?: string;
}

interface ChecklistGroup {
  title: string;
  items: ChecklistItem[];
}

interface QuickCommand {
  label: string;
  command: string;
  description?: string;
}

interface MilestoneCheck {
  label: string;
  description: string;
  critical?: boolean;
}

interface Milestone {
  number: number;
  title: string;
  description: string;
  checks: MilestoneCheck[];
}

interface CommonMistake {
  id: number;
  title: string;
  what: string;
  how: string;
}

// ─── Data ─────────────────────────────────────────────────────────

const QUICK_COMMANDS: QuickCommand[] = [
  {
    label: 'Full Deploy',
    command: 'cd C:\\Neo\\ldaja\\aidlc-admin && python create-deploy-full.py && .\\deploy-quick.ps1',
    description: 'Build, package (all 5 milestones), and deploy',
  },
  {
    label: 'Health Check',
    command: 'curl https://ldaja.ai/api/health',
    description: 'Verify the API is healthy after deployment',
  },
  {
    label: 'Verify Env',
    command: 'Get-Content C:\\Neo\\ldaja\\aidlc-admin\\.env.production | Select-String "BYPASS"',
    description: 'Confirm DEV_AUTH_BYPASS is false',
  },
  {
    label: 'Compliance Pages',
    command: '@("/privacy","/sms-consent","/sms-terms","/ivr-consent","/ivr-terms") | % { Write-Host "$_ -> $((Invoke-WebRequest "https://ldaja.ai$_" -UseBasicParsing).StatusCode)" }',
    description: 'Check all compliance pages return 200',
  },
  {
    label: 'Log Tail',
    command: '& "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd" webapp log tail --resource-group rg-ldaja-aidlc-prod --name app-ldaja-aidlc-web',
    description: 'Live tail App Service logs',
  },
  {
    label: 'Restart App',
    command: '& "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd" webapp restart --resource-group rg-ldaja-aidlc-prod --name app-ldaja-aidlc-web',
    description: 'Restart without redeploying',
  },
];

const CHECKLIST_GROUPS: ChecklistGroup[] = [
  {
    title: 'Environment Files',
    items: [
      { id: 'env-exists', label: '.env.production exists', detail: 'C:\\Neo\\ldaja\\aidlc-admin\\.env.production' },
      { id: 'env-bypass', label: 'VITE_DEV_AUTH_BYPASS=false', detail: 'Not true, not missing -- explicitly false' },
      { id: 'env-client-id', label: 'VITE_MSAL_CLIENT_ID is set', detail: 'See .env.production' },
      { id: 'env-tenant-id', label: 'VITE_MSAL_TENANT_ID is set', detail: 'See .env.production' },
      { id: 'env-redirect', label: 'VITE_MSAL_REDIRECT_URI=https://ldaja.ai/auth' },
      { id: 'env-api-base', label: 'VITE_API_BASE_URL=/api' },
      { id: 'env-ws-url', label: 'VITE_WS_URL=https://ldaja.ai' },
    ],
  },
  {
    title: 'Source Code',
    items: [
      { id: 'src-server-tsc', label: 'Server TypeScript compiles clean', detail: 'npx tsc --noEmit in server/' },
      { id: 'src-front-tsc', label: 'Frontend TypeScript compiles clean', detail: 'npx tsc --noEmit in root' },
      { id: 'src-git-clean', label: 'No uncommitted changes that should be included', detail: 'git status' },
      { id: 'src-branch', label: 'Current branch is the intended deployment branch' },
    ],
  },
  {
    title: 'Public HTML Pages',
    items: [
      { id: 'html-privacy', label: 'privacy.html exists in public/', detail: '/privacy' },
      { id: 'html-sms-consent', label: 'sms-consent.html exists in public/', detail: '/sms-consent' },
      { id: 'html-sms-terms', label: 'sms-terms.html exists in public/', detail: '/sms-terms' },
      { id: 'html-ivr-consent', label: 'ivr-consent.html exists in public/', detail: '/ivr-consent' },
      { id: 'html-ivr-terms', label: 'ivr-terms.html exists in public/', detail: '/ivr-terms' },
      { id: 'html-landing', label: 'landing.html exists in public/', detail: '/ (root)' },
    ],
  },
  {
    title: 'Prerequisites',
    items: [
      { id: 'prereq-node', label: 'Node.js installed', detail: 'For npx vite build and npx tsc' },
      { id: 'prereq-python', label: 'Python 3 installed', detail: 'For create-deploy-full.py' },
      { id: 'prereq-az', label: 'Azure CLI installed', detail: 'C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd' },
      { id: 'prereq-az-login', label: 'Logged into Azure CLI', detail: 'az account show should return LDAJA subscription' },
    ],
  },
  {
    title: 'Post-Deploy Verification',
    items: [
      { id: 'post-health', label: 'Health endpoint returns healthy', detail: 'https://ldaja.ai/api/health' },
      { id: 'post-version', label: 'Version matches expected (2.0.0)' },
      { id: 'post-compliance', label: 'All 6 compliance pages return 200' },
      { id: 'post-auth', label: 'MSAL login prompt appears in incognito', detail: 'CRITICAL: No admin access without login' },
      { id: 'post-no-bypass', label: 'No "Dev Bypass" indication visible' },
      { id: 'post-ws', label: 'WebSocket connection established', detail: 'wss://ldaja.ai, 101 Switching Protocols' },
      { id: 'post-agents', label: 'Agent status cards render after login' },
    ],
  },
];

const MILESTONES: Milestone[] = [
  {
    number: 1,
    title: 'Pre-flight Checks',
    description: 'Validates environment files, project structure, and critical configuration before any build step runs.',
    checks: [
      { label: '.env.production exists', description: 'File is present on disk' },
      { label: 'server/tsconfig.json exists', description: 'Server project is properly structured' },
      { label: 'package.json (root) exists', description: 'Frontend project is properly structured' },
      { label: 'server/package.json exists', description: 'Server dependencies are defined' },
      { label: 'VITE_DEV_AUTH_BYPASS != true', description: 'Auth would be bypassed in production', critical: true },
      { label: 'VITE_MSAL_CLIENT_ID is non-empty', description: 'Login would fail silently' },
    ],
  },
  {
    number: 2,
    title: 'Build Frontend and Server',
    description: 'Compiles the React frontend with production env vars and the Express server TypeScript.',
    checks: [
      { label: 'Frontend build', description: 'npx vite build --mode production' },
      { label: 'Server build', description: 'npx tsc (in server/)' },
      { label: 'Copy public HTML', description: 'Copies all .html from public/ to dist/' },
    ],
  },
  {
    number: 3,
    title: 'Verify Build Output',
    description: 'The safety net. Scans compiled output for auth bypass leaks, missing files, and route integrity.',
    checks: [
      { label: 'dist/index.html exists', description: 'Frontend built successfully' },
      { label: 'server/dist/index.js exists', description: 'Server compiled' },
      { label: 'JS bundle scan for DEV_AUTH_BYPASS', description: 'No JS file contains DEV_AUTH_BYPASS set to true', critical: true },
      { label: 'All 6 compliance HTML files in dist/', description: 'Privacy, SMS, IVR pages present' },
      { label: 'Server routes check', description: 'All compliance routes found in compiled server code' },
    ],
  },
  {
    number: 4,
    title: 'Package Deployment Zip',
    description: 'Creates deploy.zip with dist/, server/dist/, server/node_modules/, and support files.',
    checks: [
      { label: 'Forward-slash paths only', description: 'Windows backslashes converted to Linux-compatible /' },
      { label: 'All required directories included', description: 'dist/, server/dist/, server/node_modules/, bridge/, agents/' },
      { label: 'startup.sh included', description: 'App Service startup script' },
    ],
  },
  {
    number: 5,
    title: 'Verify Zip Contents',
    description: 'Final gate. Inspects the zip file to ensure nothing critical was missed or leaked.',
    checks: [
      { label: 'All 9 required entries present', description: 'Key files and paths verified' },
      { label: 'No backslash paths', description: 'Azure App Service runs Linux', critical: true },
      { label: 'No .env files in zip', description: 'Prevents leaking secrets', critical: true },
    ],
  },
];

const COMMON_MISTAKES: CommonMistake[] = [
  {
    id: 1,
    title: 'DEV_AUTH_BYPASS Shipped as true',
    what: 'Production was built using npx vite build without --mode production. Vite read .env (BYPASS=true) and baked it into the JS bundles. Admin portal loaded without login.',
    how: 'Milestone 1 verifies .env.production has BYPASS=false. Milestone 2 runs build with --mode production. Milestone 3 scans every .js bundle for the pattern DEV_AUTH_BYPASS.*true.',
  },
  {
    id: 2,
    title: 'Missing Public HTML Pages',
    what: 'Vite build deletes and recreates dist/. Public HTML pages that were manually placed there were lost. Compliance routes returned 404.',
    how: 'Milestone 2 copies all .html from public/ to dist/ after Vite build. Milestone 3 verifies each required HTML file exists. Milestone 5 verifies them in the final zip.',
  },
  {
    id: 3,
    title: 'Backslash Paths in Zip',
    what: 'A Windows-built zip contained paths like dist\\index.html. On the Azure Linux container, files ended up in wrong locations.',
    how: 'Milestone 4 converts all paths: arcname.replace("\\\\", "/"). Milestone 5 scans every zip entry for backslash characters.',
  },
  {
    id: 4,
    title: '.env Files in Zip',
    what: 'A deployment package accidentally included .env or .env.production, exposing MSAL client IDs and configuration.',
    how: 'Milestone 4 only copies specific directories. Milestone 5 scans all zip entries for any file whose basename starts with .env.',
  },
  {
    id: 5,
    title: 'Missing Server Routes',
    what: 'A server code change removed a compliance route. Deployment went through but compliance pages returned 404.',
    how: 'Milestone 3 reads server/dist/index.js and searches for string literals /ivr-consent, /ivr-terms, /sms-consent, /sms-terms, and /privacy.',
  },
  {
    id: 6,
    title: 'Deploying Stale dist/ with --skip-build',
    what: 'Developer used --skip-build thinking dist/ was current, but it was from a previous build with different env vars.',
    how: '--skip-build still runs Milestone 3 (build output verification) and Milestone 5 (zip verification). Full build (without --skip-build) is recommended for all production deployments.',
  },
];

const AZURE_RESOURCES = [
  { key: 'Resource Group', value: 'rg-ldaja-aidlc-prod' },
  { key: 'App Service Name', value: 'app-ldaja-aidlc-web' },
  { key: 'Production URL', value: 'https://ldaja.ai' },
  { key: 'Startup Command', value: 'bash startup.sh' },
  { key: 'Node Entry Point', value: 'server/dist/index.js' },
  { key: 'Health Endpoint', value: 'https://ldaja.ai/api/health' },
  { key: 'Deploy Script', value: 'create-deploy-full.py' },
  { key: 'Quick Deploy', value: 'deploy-quick.ps1' },
  { key: 'Full Deploy (with startup)', value: 'deploy-with-startup.ps1' },
  { key: 'Log Retrieval', value: 'get-docker-logs2.ps1' },
];

const DEPLOY_TIMING = [
  { phase: 'Zip upload', duration: '1-2 minutes' },
  { phase: 'Extraction', duration: '30-60 seconds' },
  { phase: 'Container restart', duration: '1-2 minutes' },
  { phase: 'Node.js startup', duration: '5-10 seconds' },
  { phase: 'Total', duration: '3-5 minutes' },
];

// ─── Copy to Clipboard Hook ──────────────────────────────────────

function useCopyToClipboard() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  return { copiedId, copy };
}

// ─── Checklist Persistence ───────────────────────────────────────

function loadChecklist(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // silent
  }
  return {};
}

function saveChecklist(state: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // silent
  }
}

// ─── Sub-Components ──────────────────────────────────────────────

/** Copy button icon -- shows checkmark after successful copy */
function CopyButton({
  text,
  id,
  copiedId,
  onCopy,
  size = 'sm',
}: {
  text: string;
  id: string;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
  size?: 'sm' | 'xs';
}) {
  const isCopied = copiedId === id;
  const sizeClass = size === 'xs' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const btnClass = size === 'xs' ? 'p-1' : 'p-1.5';

  return (
    <button
      onClick={() => onCopy(text, id)}
      className={`${btnClass} rounded hover:bg-white/10 transition-colors group flex-shrink-0`}
      title={isCopied ? 'Copied!' : 'Copy to clipboard'}
      aria-label={isCopied ? 'Copied to clipboard' : 'Copy to clipboard'}
    >
      {isCopied ? (
        <svg className={`${sizeClass} text-green-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        <svg className={`${sizeClass} text-gray-400 group-hover:text-white transition-colors`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
        </svg>
      )}
    </button>
  );
}

/** Code block with dark background and copy button */
function CodeBlock({
  code,
  id,
  copiedId,
  onCopy,
  language = 'powershell',
}: {
  code: string;
  id: string;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
  language?: string;
}) {
  return (
    <div className="relative group rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/50 border-b border-gray-700/50">
        <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">{language}</span>
        <CopyButton text={code} id={id} copiedId={copiedId} onCopy={onCopy} size="xs" />
      </div>
      <pre className="p-3 overflow-x-auto text-xs leading-relaxed">
        <code className="text-gray-300 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

/** Collapsible section with chevron toggle */
function CollapsibleSection({
  title,
  subtitle,
  children,
  defaultOpen = false,
  badge,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ldaja-black">{title}</span>
            {badge}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </button>
      {isOpen && (
        <div className="p-4 border-t border-gray-100 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

/** Milestone checkpoint visual card */
function MilestoneCard({
  milestone,
}: {
  milestone: Milestone;
}) {
  // Milestone status is not tracked via API -- these are informational.
  // The "status" here indicates whether the milestone ran, which we
  // cannot know from the frontend. We show them as informational.
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        {/* Milestone Number Badge */}
        <div className="w-8 h-8 rounded-full bg-ldaja-blue flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-white">{milestone.number}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-ldaja-black">{milestone.title}</h4>
          <p className="text-xs text-gray-500">{milestone.description}</p>
        </div>
      </div>
      <div className="p-3">
        <table className="w-full">
          <tbody>
            {milestone.checks.map((check, idx) => (
              <tr key={idx} className="border-b border-gray-50 last:border-0">
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    {check.critical ? (
                      <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <span className="text-xs font-medium text-gray-700">{check.label}</span>
                    {check.critical && (
                      <Badge variant="danger" className="ml-1">CRITICAL</Badge>
                    )}
                  </div>
                </td>
                <td className="py-2 text-xs text-gray-500 text-right">{check.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export function DeploymentGuidePage() {
  const [checked, setChecked] = useState<Record<string, boolean>>(loadChecklist);
  const { copiedId, copy } = useCopyToClipboard();

  // Persist checklist state
  useEffect(() => {
    saveChecklist(checked);
  }, [checked]);

  const toggleCheck = useCallback((id: string) => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const resetChecklist = useCallback(() => {
    setChecked({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Calculate checklist progress
  const totalItems = CHECKLIST_GROUPS.reduce((sum, group) => sum + group.items.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progressPercent = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Page Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Deployment Guide</h1>
          <p className="text-sm text-gray-500 mt-1">
            AIDLC Admin Portal v2.0.0 -- Production deployment to Azure App Service
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="info">Azure App Service</Badge>
            <Badge variant="neutral">app-ldaja-aidlc-web</Badge>
            <Badge variant="success" dot>https://ldaja.ai</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Last Updated</p>
          <p className="text-xs font-medium text-gray-600">2026-02-12</p>
          <p className="text-[10px] text-gray-400 mt-1">Authors</p>
          <p className="text-xs text-gray-600">Dan (Ops), Todd (Network)</p>
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Quick Reference Card
          UX: This is the most commonly-needed section for experienced
          deployers. Front-loading it saves scrolling on repeat visits.
         ──────────────────────────────────────────────────────────── */}
      <Card
        title="Quick Reference"
        subtitle="One-liner commands for experienced deployers"
        action={
          <Badge variant="info" dot>PowerShell</Badge>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {QUICK_COMMANDS.map((cmd) => (
            <div
              key={cmd.label}
              className="group relative bg-gray-900 rounded-lg p-3 hover:ring-1 hover:ring-ldaja-blue/30 transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <span className="text-xs font-semibold text-white">{cmd.label}</span>
                  {cmd.description && (
                    <p className="text-[10px] text-gray-500 mt-0.5">{cmd.description}</p>
                  )}
                </div>
                <CopyButton
                  text={cmd.command}
                  id={`quick-${cmd.label}`}
                  copiedId={copiedId}
                  onCopy={copy}
                  size="xs"
                />
              </div>
              <code className="text-[11px] text-ldaja-cyan font-mono break-all leading-relaxed block">
                {cmd.command}
              </code>
            </div>
          ))}
        </div>
      </Card>

      {/* ────────────────────────────────────────────────────────────
          Azure Resources Quick Reference
         ──────────────────────────────────────────────────────────── */}
      <Card title="Azure Resources" subtitle="Production infrastructure identifiers">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {AZURE_RESOURCES.map((res) => (
            <div key={res.key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs text-gray-500">{res.key}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-gray-700 font-mono">{res.value}</span>
                <CopyButton text={res.value} id={`res-${res.key}`} copiedId={copiedId} onCopy={copy} size="xs" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ────────────────────────────────────────────────────────────
          Pre-Deployment Checklist
          UX: Interactive checkboxes persisted in localStorage so
          operators can track progress across page reloads. The
          progress bar provides visual feedback and motivation.
         ──────────────────────────────────────────────────────────── */}
      <Card
        title="Pre-Deployment Checklist"
        subtitle={`${checkedCount} of ${totalItems} items complete`}
        action={
          <div className="flex items-center gap-2">
            {progressPercent === 100 && (
              <Badge variant="success" dot>All Clear</Badge>
            )}
            <Button variant="ghost" size="sm" onClick={resetChecklist}>
              Reset
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Progress Bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Progress</span>
              <span className="text-xs font-medium text-gray-700">{progressPercent}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${
                  progressPercent === 100
                    ? 'bg-green-500'
                    : progressPercent >= 50
                    ? 'bg-ldaja-blue'
                    : 'bg-ldaja-cyan'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Checklist Groups */}
          {CHECKLIST_GROUPS.map((group) => {
            const groupChecked = group.items.filter(item => checked[item.id]).length;
            const groupComplete = groupChecked === group.items.length;

            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-xs font-semibold text-gray-700">{group.title}</h4>
                  <span className={`text-[10px] ${groupComplete ? 'text-green-600' : 'text-gray-400'}`}>
                    {groupChecked}/{group.items.length}
                  </span>
                  {groupComplete && (
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  )}
                </div>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <label
                      key={item.id}
                      className={`flex items-start gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                        checked[item.id]
                          ? 'bg-green-50/50 hover:bg-green-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50 flex-shrink-0"
                        checked={!!checked[item.id]}
                        onChange={() => toggleCheck(item.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs ${checked[item.id] ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                          {item.label}
                        </span>
                        {item.detail && (
                          <span className="text-[10px] text-gray-400 block mt-0.5 font-mono">
                            {item.detail}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* ────────────────────────────────────────────────────────────
          Milestone Checkpoints
          UX: Visual pipeline representation showing the 5 gates.
          Each milestone is a standalone card to make scanning easy.
         ──────────────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-bold text-ldaja-black">Milestone Checkpoints</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              5 hard gates in create-deploy-full.py -- if any fails, no zip is created
            </p>
          </div>
          <Badge variant="warning">Hard Gates</Badge>
        </div>

        {/* Milestone Pipeline Visual */}
        <div className="flex items-center gap-0 mb-6 overflow-x-auto pb-2">
          {MILESTONES.map((m, idx) => (
            <React.Fragment key={m.number}>
              {idx > 0 && (
                <div className="w-8 h-0.5 bg-gray-300 flex-shrink-0" />
              )}
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0 min-w-[100px]">
                <div className="w-10 h-10 rounded-full bg-ldaja-blue/10 border-2 border-ldaja-blue flex items-center justify-center">
                  <span className="text-sm font-bold text-ldaja-blue">{m.number}</span>
                </div>
                <span className="text-[10px] text-gray-600 text-center font-medium leading-tight max-w-[100px]">
                  {m.title}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Milestone Detail Cards */}
        <div className="space-y-3">
          {MILESTONES.map((m) => (
            <MilestoneCard
              key={m.number}
              milestone={m}
            />
          ))}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Build Phase Details
          UX: Collapsible to reduce initial page weight. Operators
          who already know the build steps can skip this entirely.
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Build Phase"
        subtitle="Step-by-step build instructions"
        badge={<Badge variant="danger">Critical: --mode production</Badge>}
      >
        <div className="space-y-4">
          {/* Warning Callout */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-red-800">Why --mode production matters</h4>
                <p className="text-xs text-red-700 mt-1 leading-relaxed">
                  Vite performs environment variable substitution at <strong>build time</strong>, not runtime.
                  Without <code className="bg-red-100 px-1 rounded">--mode production</code>, Vite reads{' '}
                  <code className="bg-red-100 px-1 rounded">.env</code> (which has BYPASS=true) and bakes it into
                  the JS bundles permanently. This caused the 2026-02-12 incident.
                </p>
              </div>
            </div>
          </div>

          {/* Build Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Command</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Reads From</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">BYPASS Value</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Result</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="px-3 py-2 font-mono text-gray-700">npx vite build</td>
                  <td className="px-3 py-2 text-gray-600">.env</td>
                  <td className="px-3 py-2"><Badge variant="danger">true</Badge></td>
                  <td className="px-3 py-2 text-red-600 font-medium">Auth bypassed</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-gray-700">npx vite build --mode production</td>
                  <td className="px-3 py-2 text-gray-600">.env.production</td>
                  <td className="px-3 py-2"><Badge variant="success">false</Badge></td>
                  <td className="px-3 py-2 text-green-600 font-medium">MSAL enforced</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Step 1 */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Step 1: Build the Frontend</h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\nnpx vite build --mode production`}
              id="build-frontend"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Step 2 */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Step 2: Build the Server</h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\\server\nnpx tsc`}
              id="build-server"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Step 3 */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Step 3: Copy Public HTML to dist/</h4>
            <p className="text-xs text-gray-500 mb-2">
              Vite overwrites the entire dist/ directory. Public HTML pages must be copied back.
            </p>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\nCopy-Item public\\privacy.html dist\\\nCopy-Item public\\sms-consent.html dist\\\nCopy-Item public\\sms-terms.html dist\\\nCopy-Item public\\ivr-consent.html dist\\\nCopy-Item public\\ivr-terms.html dist\\\nCopy-Item public\\landing.html dist\\`}
              id="build-copy-html"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Verify */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Build Verification</h4>
            <p className="text-xs text-gray-500 mb-2">
              Search JS bundles for the bypass flag -- if this returns results containing "true", rebuild.
            </p>
            <CodeBlock
              code={`Select-String -Path "C:\\Neo\\ldaja\\aidlc-admin\\dist\\assets\\*.js" -Pattern "DEV_AUTH_BYPASS"`}
              id="build-verify"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Package Phase
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Package Phase"
        subtitle="Automated build + verification + packaging via create-deploy-full.py"
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Full Build (Recommended)</h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\npython create-deploy-full.py`}
              id="package-full"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Slim Build (smaller zip, no source maps)</h4>
            <CodeBlock
              code={`python create-deploy-full.py --slim`}
              id="package-slim"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Skip Build (only if manually built with --mode production)</h4>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
              <p className="text-xs text-amber-800">
                <strong>Warning:</strong> --skip-build trusts that the existing dist/ was built correctly.
                Only use this if you literally just ran the build commands and verified them.
              </p>
            </div>
            <CodeBlock
              code={`python create-deploy-full.py --skip-build`}
              id="package-skip"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Zip Contents Table */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Zip Contents</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Zip Path</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Source</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Purpose</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {[
                    { path: 'dist/', source: 'Frontend build', purpose: 'React SPA + static HTML' },
                    { path: 'server/dist/', source: 'Server TS compilation', purpose: 'Express server JS' },
                    { path: 'server/node_modules/', source: 'Server deps', purpose: 'Runtime packages' },
                    { path: 'server/package.json', source: 'Server manifest', purpose: 'Used by Node.js' },
                    { path: 'server/data/*.json', source: 'Archive/seed data', purpose: 'Compliance, seeds' },
                    { path: 'bridge/', source: 'Bridge scripts', purpose: 'Claude Code integration' },
                    { path: 'agents/', source: 'Agent files', purpose: 'Agent .md definitions' },
                    { path: 'startup.sh', source: 'Startup script', purpose: 'node server/dist/index.js' },
                  ].map((row) => (
                    <tr key={row.path} className="border-b border-gray-50">
                      <td className="px-3 py-2 text-ldaja-blue">{row.path}</td>
                      <td className="px-3 py-2 font-sans text-gray-600">{row.source}</td>
                      <td className="px-3 py-2 font-sans text-gray-500">{row.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Deploy Phase
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Deploy Phase"
        subtitle="Azure CLI deployment and verification"
        badge={<Badge variant="info">3-5 minutes total</Badge>}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Standard Deployment (PowerShell)</h4>
            <CodeBlock
              code={`$az = "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd"\n& $az webapp deployment source config-zip \`\n  --resource-group rg-ldaja-aidlc-prod \`\n  --name app-ldaja-aidlc-web \`\n  --src "C:\\Neo\\ldaja\\aidlc-admin\\deploy.zip" \`\n  --timeout 300`}
              id="deploy-standard"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Quick Deploy Script</h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\n.\\deploy-quick.ps1`}
              id="deploy-quick"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Full Deploy Script (sets startup command + managed identity)</h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\n.\\deploy-with-startup.ps1`}
              id="deploy-full"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Expected Timing */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Expected Timing</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              {DEPLOY_TIMING.map((row) => (
                <div
                  key={row.phase}
                  className={`flex items-center justify-between py-1.5 text-xs ${
                    row.phase === 'Total' ? 'border-t border-gray-200 mt-1 pt-2 font-semibold' : ''
                  }`}
                >
                  <span className={row.phase === 'Total' ? 'text-ldaja-black' : 'text-gray-600'}>
                    {row.phase}
                  </span>
                  <span className={row.phase === 'Total' ? 'text-ldaja-blue font-bold' : 'text-gray-700 font-medium'}>
                    {row.duration}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* What Happens During Deployment */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">What Happens During Deployment</h4>
            <ol className="space-y-2 text-xs text-gray-600 list-none">
              {[
                'Azure CLI uploads deploy.zip to App Service via the Kudu ZIP deploy API',
                'Kudu extracts the zip into /home/site/wwwroot/ on the Linux container',
                'App Service detects the new deployment and restarts the container',
                'The startup command (bash startup.sh) runs node server/dist/index.js',
                'Express starts, serves the React SPA from dist/, and listens on the configured port',
              ].map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-ldaja-blue">{idx + 1}</span>
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Check Deployment Status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Check Deployment Status</h4>
            <CodeBlock
              code={`$az = "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd"\n& $az webapp deployment list --resource-group rg-ldaja-aidlc-prod --name app-ldaja-aidlc-web --query "[0].{status:status,time:endTime,active:active}" -o table`}
              id="deploy-status"
              copiedId={copiedId}
              onCopy={copy}
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Wait at least 30 seconds after the deployment command completes before running verification.
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Post-Deploy Verification
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Post-Deploy Verification"
        subtitle="Health check, compliance pages, auth verification, WebSocket"
        badge={<Badge variant="danger">Do not skip</Badge>}
      >
        <div className="space-y-4">
          {/* 5.1 Health Check */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Health Check</h4>
            <CodeBlock
              code={`Invoke-WebRequest -Uri "https://ldaja.ai/api/health" -UseBasicParsing | Select-Object -ExpandProperty Content`}
              id="verify-health"
              copiedId={copiedId}
              onCopy={copy}
            />
            <div className="mt-2 bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-1">Expected response:</p>
              <pre className="text-[11px] text-gray-600 font-mono">
{`{
  "status": "healthy",
  "version": "2.0.0",
  "timestamp": "2026-02-12T...",
  "uptime": ...
}`}
              </pre>
            </div>
          </div>

          {/* 5.2 Compliance Pages */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Compliance Pages (All Must Return 200)</h4>
            <CodeBlock
              code={`$pages = @("/privacy", "/sms-consent", "/sms-terms", "/ivr-consent", "/ivr-terms", "/")\nforeach ($page in $pages) {\n    try {\n        $r = Invoke-WebRequest -Uri "https://ldaja.ai$page" -UseBasicParsing -TimeoutSec 10\n        Write-Host "$page -> $($r.StatusCode) OK"\n    } catch {\n        Write-Host "$page -> FAILED: $($_.Exception.Message)"\n    }\n}`}
              id="verify-compliance"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* 5.3 Auth Verification */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-red-800 mb-2">Authentication Verification (CRITICAL)</h4>
            <p className="text-xs text-red-700 mb-2">
              Open <code className="bg-red-100 px-1 rounded">https://ldaja.ai/admin</code> in an incognito/private browser window.
            </p>
            <ul className="space-y-1.5 text-xs text-red-700">
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                You see the Microsoft MSAL login prompt (Azure AD sign-in page)
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                You do NOT see the admin dashboard without logging in
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                You do NOT see "Dev Bypass" or any indication that auth is skipped
              </li>
            </ul>
            <p className="text-xs text-red-800 font-semibold mt-3">
              If the admin portal loads without requiring login, the build was done incorrectly. Stop everything and redeploy.
            </p>
          </div>

          {/* 5.4 & 5.5 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">WebSocket Connection</h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>Open DevTools (F12) &gt; Network &gt; WS tab</li>
                <li>Verify WebSocket to wss://ldaja.ai</li>
                <li>Status: "Open" or "101 Switching Protocols"</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Agent Status</h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>Navigate to Overview or Agents tab</li>
                <li>Verify agent status cards are rendering</li>
                <li>Running agents should update in real-time</li>
              </ul>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Rollback Procedure
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Rollback Procedure"
        subtitle="Three options for rolling back a bad deployment"
      >
        <div className="space-y-4">
          {/* Option A */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-1">
              Option A: Redeploy Previous Zip
            </h4>
            <p className="text-xs text-gray-500 mb-2">If you kept the previous deploy.zip (renamed or archived):</p>
            <CodeBlock
              code={`$az = "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd"\n& $az webapp deployment source config-zip \`\n  --resource-group rg-ldaja-aidlc-prod \`\n  --name app-ldaja-aidlc-web \`\n  --src "C:\\Neo\\ldaja\\aidlc-admin\\deploy-previous.zip" \`\n  --timeout 300`}
              id="rollback-a"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Option B */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-1">
              Option B: Rebuild from a Known-Good Commit
            </h4>
            <CodeBlock
              code={`cd C:\\Neo\\ldaja\\aidlc-admin\ngit log --oneline -10                  # Find the last known-good commit\ngit checkout <commit-hash>             # Check out that commit\npython create-deploy-full.py           # Rebuild and repackage\n.\\deploy-quick.ps1                     # Redeploy\ngit checkout -                         # Return to previous branch`}
              id="rollback-b"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Option C */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-1">
              Option C: Restart App Service (No Code Change)
            </h4>
            <p className="text-xs text-gray-500 mb-2">For container crashes or transient failures, not code problems:</p>
            <CodeBlock
              code={`$az = "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd"\n& $az webapp restart --resource-group rg-ldaja-aidlc-prod --name app-ldaja-aidlc-web`}
              id="rollback-c"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>

          {/* Logs */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Checking App Service Logs</h4>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] text-gray-500 mb-1">Quick log tail:</p>
                <CodeBlock
                  code={`$az = "C:\\Program Files\\Microsoft SDKs\\Azure\\CLI2\\wbin\\az.cmd"\n& $az webapp log tail --resource-group rg-ldaja-aidlc-prod --name app-ldaja-aidlc-web`}
                  id="logs-tail"
                  copiedId={copiedId}
                  onCopy={copy}
                />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 mb-1">Download full Docker/container logs:</p>
                <CodeBlock
                  code={`cd C:\\Neo\\ldaja\\aidlc-admin\n.\\get-docker-logs2.ps1`}
                  id="logs-docker"
                  copiedId={copiedId}
                  onCopy={copy}
                />
              </div>
            </div>
          </div>

          {/* Best Practice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-blue-800 mb-1">Best Practice: Archive Before Deploy</h4>
            <p className="text-xs text-blue-700 mb-2">Before every deployment, rename the current zip so you have a rollback artifact:</p>
            <CodeBlock
              code={`$timestamp = Get-Date -Format "yyyy-MM-dd-HHmm"\nif (Test-Path "C:\\Neo\\ldaja\\aidlc-admin\\deploy.zip") {\n    Move-Item "C:\\Neo\\ldaja\\aidlc-admin\\deploy.zip" "C:\\Neo\\ldaja\\aidlc-admin\\deploy-$timestamp.zip"\n}`}
              id="archive-best-practice"
              copiedId={copiedId}
              onCopy={copy}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Common Mistakes
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Common Mistakes and How the Script Prevents Them"
        subtitle="6 known failure modes with automated safeguards"
        badge={<Badge variant="warning">{COMMON_MISTAKES.length} patterns</Badge>}
      >
        <div className="space-y-3">
          {COMMON_MISTAKES.map((mistake) => (
            <div key={mistake.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-red-600">{mistake.id}</span>
                </span>
                <span className="text-xs font-semibold text-gray-800">{mistake.title}</span>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">What happened</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{mistake.what}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider mb-0.5">How the script prevents it</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{mistake.how}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          Environment Variable Flow (Appendix)
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Appendix: Environment Variable Flow"
        subtitle="How VITE_DEV_AUTH_BYPASS travels from .env to the user's browser"
      >
        <div className="space-y-4">
          {/* Visual Flow */}
          <div className="bg-gray-900 rounded-lg p-4">
            <div className="space-y-3">
              {[
                { label: '.env.production', desc: 'VITE_DEV_AUTH_BYPASS=false', color: 'text-yellow-400' },
                { label: 'npx vite build --mode production', desc: 'Reads .env.production', color: 'text-blue-400' },
                { label: 'dist/assets/index-XXXXX.js', desc: '"false" is hardcoded in the bundle', color: 'text-green-400' },
                { label: 'Milestone 3 scan', desc: 'Verifies no "true" leaks', color: 'text-purple-400' },
                { label: 'deploy.zip -> Azure -> Browser', desc: 'User loads the bundle', color: 'text-cyan-400' },
                { label: 'MSAL login enforced', desc: 'Bypass check reads "false"', color: 'text-green-400' },
              ].map((step, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-gray-700 border-2 border-gray-500" />
                    {idx < 5 && <div className="w-0.5 h-4 bg-gray-700" />}
                  </div>
                  <div>
                    <span className={`text-xs font-mono font-medium ${step.color}`}>{step.label}</span>
                    <p className="text-[10px] text-gray-500">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800 leading-relaxed">
              Without <code className="bg-amber-100 px-1 rounded">--mode production</code>, the flow reads{' '}
              <code className="bg-amber-100 px-1 rounded">.env</code> instead of{' '}
              <code className="bg-amber-100 px-1 rounded">.env.production</code>, and "true" gets baked in.
              The browser then evaluates the bypass as enabled and skips MSAL entirely.
              There is no server-side safety net -- it is a purely client-side decision.
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* ────────────────────────────────────────────────────────────
          File Layout (Appendix)
         ──────────────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Appendix: File Layout in Azure App Service"
        subtitle="What the container looks like at /home/site/wwwroot/ after deployment"
      >
        <div className="bg-gray-900 rounded-lg p-4">
          <pre className="text-xs text-gray-300 font-mono leading-relaxed">
{`/home/site/wwwroot/
  startup.sh                    # Startup script
  dist/
    index.html                  # React SPA entry point
    assets/
      index-XXXXX.js            # Bundled React app (env vars baked in)
      index-XXXXX.css           # Bundled styles
    privacy.html                # Compliance: privacy policy
    sms-consent.html            # Compliance: SMS consent
    sms-terms.html              # Compliance: SMS terms
    ivr-consent.html            # Compliance: IVR consent
    ivr-terms.html              # Compliance: IVR terms
    landing.html                # Public landing page
  server/
    dist/
      index.js                  # Compiled Express server
      routes/                   # Compiled route handlers
      services/                 # Compiled service modules
    node_modules/               # Server runtime dependencies
    package.json                # Server package manifest
    data/
      *.json                    # Archive/seed data files
  bridge/
    report.js                   # Agent status reporting
    report-audit.js             # Audit log reporting
    agent-dispatcher.js         # Agent dispatcher
    agent-status.json           # Current agent status file
  agents/
    *.md                        # Agent instruction files`}
          </pre>
        </div>
      </CollapsibleSection>

      {/* Footer */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          This guide was created after the 2026-02-12 incident where VITE_DEV_AUTH_BYPASS=true was shipped to production.
        </p>
        <p className="text-[10px] text-gray-300 mt-0.5">
          Every check in this document and in create-deploy-full.py exists to prevent that class of error from reaching production again.
        </p>
      </div>
    </div>
  );
}
